import type { FeedItem, FeedPost, FeedPortfolio } from "../types/feed.ts"
import { buildProfileUrl, getUserDisplayName } from "./navigationHelpers.ts"

export const DASHBOARD_PREVIEW_TARGET = 3
export const DASHBOARD_PREVIEW_FETCH_LIMIT = 15

const MIN_POST_TEXT_LENGTH = 24
const MIN_PORTFOLIO_BODY_LENGTH = 20

export type FeedPreviewMediaKind = "image" | "video"

export type FeedPreviewCardModel = {
  key: string
  itemType: "post" | "portfolio"
  itemId: number
  mediaKind: FeedPreviewMediaKind
  mediaUrl: string
  posterUrl: string | null
  title: string
  authorName: string
  badge: "Posteo" | "Producto" | "Servicio"
  profileUrl: string
  likesCount?: number
}

function truncate(value: string, max: number): string {
  const trimmed = value.trim()
  if (trimmed.length <= max) return trimmed
  return `${trimmed.slice(0, max).trimEnd()}…`
}

export function getPostImageUrl(post: FeedPost): string | null {
  const url = post.images?.[0]?.image?.trim()
  return url || null
}

export function getPostVideoUrl(post: FeedPost): string | null {
  const url = post.video?.[0]?.video?.trim()
  return url || null
}

export function getPortfolioImageUrl(portfolio: FeedPortfolio): string | null {
  const url = portfolio.portfolio_images?.[0]?.image?.trim()
  return url || null
}

export function hasDisplayMedia(item: FeedItem): boolean {
  if (item.itemType === "post") {
    return Boolean(getPostImageUrl(item) || getPostVideoUrl(item))
  }
  return Boolean(getPortfolioImageUrl(item))
}

export function hasReadableText(item: FeedItem): boolean {
  if (item.itemType === "post") {
    return (item.content ?? "").trim().length >= MIN_POST_TEXT_LENGTH
  }
  const title = (item.name ?? "").trim()
  const body = (item.description ?? "").trim()
  return title.length >= 3 && body.length >= MIN_PORTFOLIO_BODY_LENGTH
}

function previewScore(item: FeedItem): number {
  let score = 0
  if (hasDisplayMedia(item)) score += 100
  if (hasReadableText(item)) score += 40
  return score
}

export function pickFeedPreviewItems(
  items: FeedItem[],
  targetCount: number = DASHBOARD_PREVIEW_TARGET
): FeedItem[] {
  const pool = items.filter((item) => previewScore(item) >= 100)
  const withText = pool.filter(hasReadableText)
  const mediaOnly = pool.filter((item) => !hasReadableText(item))
  return [...withText, ...mediaOnly].slice(0, targetCount)
}

function formatAuthorLabel(item: FeedItem): string {
  const user = item.user
  if (!user) return "Usuario"
  const fromHelper = getUserDisplayName(user)
  if (fromHelper) return fromHelper
  if (user.type === "COMPANY") {
    return (user as { razon_social?: string }).razon_social?.trim() || "Empresa"
  }
  const parts = [user.name, user.last_name].filter(Boolean).join(" ").trim()
  return parts || "Usuario"
}

function getFeedPreviewProfileUrl(item: FeedItem): string | null {
  const userId = item.user?.id
  if (userId == null || userId === 0) return null
  const displayName = getUserDisplayName(item.user) ?? formatAuthorLabel(item)
  if (item.itemType === "post") {
    return buildProfileUrl(String(userId), displayName, "Muro")
  }
  return buildProfileUrl(String(userId), displayName, "Productos/Servicios")
}

function resolveMedia(item: FeedItem): {
  mediaKind: FeedPreviewMediaKind
  mediaUrl: string
  posterUrl: string | null
} | null {
  if (item.itemType === "post") {
    const videoUrl = getPostVideoUrl(item)
    const imageUrl = getPostImageUrl(item)
    if (videoUrl) {
      return {
        mediaKind: "video",
        mediaUrl: videoUrl,
        posterUrl: imageUrl,
      }
    }
    if (imageUrl) {
      return { mediaKind: "image", mediaUrl: imageUrl, posterUrl: null }
    }
    return null
  }
  const imageUrl = getPortfolioImageUrl(item)
  if (!imageUrl) return null
  return { mediaKind: "image", mediaUrl: imageUrl, posterUrl: null }
}

export function getPostLikesCount(post: FeedPost): number {
  const likesArray = post.likes ?? []
  const likesCountHint =
    typeof post.likesCount === "number" ? post.likesCount : 0
  return Math.max(likesArray.length, likesCountHint)
}

function resolveTitle(item: FeedItem): string {
  if (item.itemType === "post") {
    const content = (item.content ?? "").trim()
    if (content) return truncate(content, 90)
    return "Publicación"
  }
  const name = (item.name ?? "").trim()
  if (name) return truncate(name, 60)
  const description = (item.description ?? "").trim()
  if (description) return truncate(description, 90)
  return item.type === "servicio" ? "Servicio" : "Producto"
}

export function toFeedPreviewCardModel(item: FeedItem): FeedPreviewCardModel | null {
  const media = resolveMedia(item)
  const profileUrl = getFeedPreviewProfileUrl(item)
  if (!media || !profileUrl) return null

  const badge: FeedPreviewCardModel["badge"] =
    item.itemType === "post"
      ? "Posteo"
      : item.type === "servicio"
        ? "Servicio"
        : "Producto"

  const likesCount =
    item.itemType === "post" ? getPostLikesCount(item) : 0

  return {
    key: `${item.itemType}-${item.id}`,
    itemType: item.itemType,
    itemId: item.id,
    mediaKind: media.mediaKind,
    mediaUrl: media.mediaUrl,
    posterUrl: media.posterUrl,
    title: resolveTitle(item),
    authorName: formatAuthorLabel(item),
    badge,
    profileUrl,
    ...(likesCount > 0 ? { likesCount } : {}),
  }
}

export function mapFeedItemsToPreviewCards(items: FeedItem[]): FeedPreviewCardModel[] {
  return pickFeedPreviewItems(items)
    .map(toFeedPreviewCardModel)
    .filter((card): card is FeedPreviewCardModel => card != null)
}
